import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRouteSnapshot, ActivationEnd, ActivationStart, Event as RouterEventBase, GuardsCheckEnd, GuardsCheckStart, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { isRouteDataWithInlineLayout, isRouteDataWithLayout, LayoutResolver } from 'src/app/layout/layout.models';
import { AdminOrSupportGuard } from 'src/app/shared/guards/admin-or-support.guard';
import * as AppSelectors from 'src/app/state/app/app.selectors';
import { InsightsLibraryActions, InsightsLibrarySelectors } from '../../insights-library/state/insights-library';
import { AppActions } from '../../state/app';
import { AppUser } from 'src/app/shared/models/app-user';
import {VolatileAppSettingsDto} from "../../services/api.services";
import { isCanvasRendererHost } from 'src/app/canvas-renderer/canvas-renderer-host';
import { logger } from '../../pbir/utils/logger';

const LOGGER = logger('LayoutShellComponent');

@Component({
  selector: 'app-layout-shell',
  templateUrl: './layout-shell.component.html',
  standalone: false
})
export class LayoutShellComponent implements OnInit, OnDestroy {
  // Opted into string layout parameters directly, rather than using state.
  // The reasoning is that because the parameters are route-driven,
  // Angular already tracks them, and there is no benefit in using state.
  // This also means the layout template is ever so slightly more responsive,
  // as layout change is applied immediately rather than on whenever Angular decides to evaluate its async bindings.
  layout: string = isCanvasRendererHost ? 'noframe' : '';
  params: any = {};

  // Tracks when the route is being changed, so we can display a progress indicator.
  // Note this only tracks the route change itself - meaning any requests firing after activation will not be considered.
  // This effectively means the indicator will display only for the time it takes to load the route's component (or lazy-loaded module).
  private loading = new BehaviorSubject<boolean>(false);
  loading$ = this.loading.asObservable().pipe(debounceTime(300));

  user$: Observable<AppUser>;

  // As logging the user out is not layout's responsibility, emit it as an event and let the app decide what to do.
  @Output() logout = new EventEmitter();

  private onRouterEventSub: Subscription;

  constructor(
    private store: Store,
    private router: Router) {
  }

  ngOnInit() {
    this.user$ = this.store.pipe(select(AppSelectors.getUser));

    this.onRouterEventSub = this.router.events.subscribe(e => this.onRouterEvent(e));
  }

  ngOnDestroy() {
    this.onRouterEventSub?.unsubscribe();
  }
  
  /* For every navigation, we merge layout data from the route data.
   * 
   * The code looks for information in route data, under the 'layout' property:
   * 
   * > If the property is an object, it attempts to find the 'name' and 'params'
   *   properties on it (to use as layout name and parameters respectively).
   *   
   * > If the property is a string, it will use the string as layout name, and look
   *   for the 'layoutParams' property on route.data to use as layout parameters.
   * 
   * Additionally, the route can specify the 'layoutIgnoreInheritedParams' property
   * in its data to indicate that parent layout parameters should not be merged in.
   * 
   * Note, parameter merging is done bottom to top (i.e. child parameters take precedence over parent parameters).
   */
  private isNavigating = false;
  private capturedLayout: string | LayoutResolver = '';
  private capturedLayoutParams: any = {};
  private stopInheritingParams = false;
  private currentRouteVersion = 0;

  private onRouterEvent(e: RouterEventBase) {
    if (e instanceof ActivationStart) {
      // Reset page component on activation start
      this.store.dispatch(AppActions.setPageComponent({ component: null }));
    }
    

    if (e instanceof NavigationStart) {
      this.currentRouteVersion++;

      this.loading.next(true);

      this.isNavigating = true;

      // null indicates the layout name has not yet been set, so start with that
      this.capturedLayout = null;
      this.capturedLayoutParams = {};
      this.stopInheritingParams = false;
    }
    else if (e instanceof NavigationEnd) {
      this.loading.next(false);

      // Keep track of the route being applied - this is needed when using async callbacks
      // for setting the layout, so we can discard them if the route changed before they finished
      let version = this.currentRouteVersion;

      if (this.isNavigating) {
        this.isNavigating = false;

        // update the store with new layout information
        if (typeof this.capturedLayout === 'function') {
          let layoutResolverResult = this.capturedLayout(this.router, this.params);

          if (typeof layoutResolverResult === 'string') {
            this.layout = layoutResolverResult;
            this.params = this.capturedLayoutParams;
          }
          else {
            // Capture current params
            let params = this.capturedLayoutParams;

            layoutResolverResult.then(l => {
              if (version !== this.currentRouteVersion) {
                LOGGER.warn('Route changed before layout has resolved');
                return;
              }

              this.layout = l;
              this.params = params;
            });
          }
        }
        else {
          this.layout = this.capturedLayout;
          this.params = this.capturedLayoutParams;
        }
      }
    }
    else if (e instanceof GuardsCheckStart) {
      this.loading.next(false);
      
      this.isNavigating = false;
    }
    else if (e instanceof GuardsCheckEnd) {
      this.loading.next(true);
      
      this.isNavigating = true;
    }
    else if (e instanceof NavigationCancel) {
      this.loading.next(false);
      
      this.isNavigating = false;
    }
    else if (e instanceof NavigationError) {
      // on error, just stop the attempt
      this.loading.next(false);

      this.isNavigating = false;
    }
    else if (this.isNavigating) {
      if (e instanceof ActivationEnd) {
        let layout;
        let params;

        let routeData = e.snapshot.data;

        if (isRouteDataWithLayout(routeData)) {
          // layout data is either:
          if (isRouteDataWithInlineLayout(routeData)) {
            // { layout:string, layoutParams: any }
            layout = routeData.layout;
            params = routeData.layoutParams;
          }
          else {
            // { layout: { name: string, params: any } }
            layout = routeData.layout.name;
            params = routeData.layout.params;
          }
        }

        if (layout !== undefined && this.capturedLayout === null) {
          // Layout name is only set once - which works, because ActivationEnd event
          // happens from the most specific route, to the least specific route (child -> parent)
          this.capturedLayout = layout;
        }

        if (params && !this.stopInheritingParams) {
          this.capturedLayoutParams = { ...params, ...this.capturedLayoutParams };
        }

        if (routeData.layoutIgnoreInheritedParams) {
          // This will cause params to not be inherited starting with any parent routes
          this.stopInheritingParams = true;
        }
      }
    }
  }


  //#region Insights Library
  insightsLibraryVisible$ = this.store.pipe(select(InsightsLibrarySelectors.isVisible));

  openInsightsLibrary() {
    this.store.dispatch(InsightsLibraryActions.setVisible({
      visible: true
    }));
  }
  //#endregion
}
